// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE ONE MESSAGE BOX (the shared composer — Jesse/Hannah, approved 2026-11-02, v3).
//
// Every message box renders this: app (borrower) Messages, DoorlyPro Messages, every DoorlyDesk staff box (through
// admin/StaffComposer) and the ticket reply / internal note. Same look, same behaviour:
//
//   [ format row: Bold · Italic · Underline · Bulleted · Numbered · Link ]   ← only when Aa is on
//   [ the box — WYSIWYG, full width, grows to about 6 lines then scrolls ]
//   [ Aa · Attach file · (type/size hint) ·············· Send ]
//
// • WHAT YOU SEE IS WHAT IS SENT. Tiptap (ProseMirror) with ONLY these nodes and marks registered: paragraph, hard
//   break, bold, italic, underline, bullet list, ordered list, list item, link. Pasting from Word or Gmail keeps only
//   those (the schema drops everything else; Word's list paragraphs become real lists, transformPastedHTML below).
//   The SERVER is the authority: it sanitizes body_html against the same allowlist on every write path
//   (backend utils/messageHtml) — nothing here is trusted.
// • Keys: Enter = a new line (or the next bullet). Ctrl/Cmd+Enter = send. On a phone, the Send button only.
//   Ctrl/Cmd+B / I / U, and Ctrl/Cmd+K for a link — also while the format row is collapsed.
// • Aa (accessible name "Formatting", aria-pressed) shows or hides the format row; the choice is remembered on this
//   device (localStorage, failures ignored).
// • Links: http, https or mailto only ("example.com" is saved as https://example.com), added and edited in a small
//   dialog (never window.prompt).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { Extension } from '@tiptap/core';
import { STATUS_COLORS } from '../constants/statusColors';

const BLUE = '#3a6ad9';
const VIOLET = '#7C58F5';          // doorly-violet — the internal note
const VIOLET_LIGHT = '#ECE5FE';    // doorly-violet-light
const NEUTRAL_LIGHTEST = '#f7f7f7';
export const FORMAT_BAR_KEY = 'doorly_composer_format_bar';

const SAFE_URL = /^(https?:\/\/[^\s/]|mailto:\S)/i;
/** "example.com" → "https://example.com"; anything that is not http(s)/mailto → null. */
export function normaliseLink(raw) {
  const v = String(raw || '').trim();
  if (!v) return null;
  if (SAFE_URL.test(v)) return v;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null;                 // another scheme (javascript:, data:, ftp:…)
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return `mailto:${v}`;   // a bare email address
  if (/^[^\s/]+\.[^\s]+$/.test(v)) return `https://${v}`;           // a bare domain or domain/path
  return null;
}

const readBar = (key) => { try { return localStorage.getItem(key) === 'open'; } catch (_) { return false; } };
const writeBar = (key, open) => { try { localStorage.setItem(key, open ? 'open' : 'closed'); } catch (_) { /* ignored */ } };

/** Word sends lists as <p class="MsoListParagraph…" style="mso-list:…">· text</p>; make them real lists. */
export function wordListsToHtml(html) {
  if (!/mso-list|MsoListParagraph/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const paras = Array.from(doc.querySelectorAll('p'));
    let list = null; let kind = null;
    for (const p of paras) {
      const isItem = /MsoListParagraph/i.test(p.className || '') || /mso-list/i.test(p.getAttribute('style') || '');
      if (!isItem) { list = null; kind = null; continue; }
      // The marker Word writes in a span (mso-list:Ignore): "·", "o", "§", "1.", "a)"…
      const ignore = Array.from(p.querySelectorAll('span')).find((s) => /mso-list:\s*ignore/i.test(s.getAttribute('style') || ''));
      const marker = (ignore ? ignore.textContent : '').trim();
      if (ignore) ignore.remove();
      const itemKind = /^[0-9a-z]{1,3}[.)]$/i.test(marker) ? 'ol' : 'ul';
      if (!list || kind !== itemKind) { list = doc.createElement(itemKind); kind = itemKind; p.parentNode.insertBefore(list, p); }
      const li = doc.createElement('li');
      li.innerHTML = p.innerHTML;
      list.appendChild(li);
      p.remove();
    }
    return doc.body.innerHTML;
  } catch (_) { return html; }
}

// ── icons: inline SVG, 24-unit box, stroke = currentColor (the paperclip / utils/icons.js idiom; no icon library) ──
const Svg = ({ size = 18, children, sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{children}</svg>
);
const ICONS = {
  format: (s) => <Svg size={s}><path d="M3 19 8 5l5 14M5 14h6" /><circle cx="17.5" cy="15.5" r="3" /><path d="M20.5 12.5V19" /></Svg>,
  bold: (s) => <Svg size={s}><path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" /></Svg>,
  italic: (s) => <Svg size={s}><path d="M10 5h8M6 19h8M14 5l-4 14" /></Svg>,
  underline: (s) => <Svg size={s}><path d="M7 4v7a5 5 0 0 0 10 0V4M5 20h14" /></Svg>,
  bullets: (s) => <Svg size={s}><path d="M9 6h11M9 12h11M9 18h11" /><path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01" strokeWidth="3" /></Svg>,
  numbers: (s) => <Svg size={s}><path d="M10 6h10M10 12h10M10 18h10M4 4.5 5.5 4v5M4 9h3M4 14.5a1.5 1.5 0 0 1 3 .2c0 1-3 2-3 3.3h3" /></Svg>,
  link: (s) => <Svg size={s}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></Svg>,
  clip: (s) => <Svg size={s}><path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></Svg>,
};
const FORMATS = [
  { k: 'bold', label: 'Bold', key: 'B', active: (e) => e.isActive('bold'), run: (e) => e.chain().focus().toggleBold().run() },
  { k: 'italic', label: 'Italic', key: 'I', active: (e) => e.isActive('italic'), run: (e) => e.chain().focus().toggleItalic().run() },
  { k: 'underline', label: 'Underline', key: 'U', active: (e) => e.isActive('underline'), run: (e) => e.chain().focus().toggleUnderline().run() },
  { k: 'bullets', label: 'Bulleted list', active: (e) => e.isActive('bulletList'), run: (e) => e.chain().focus().toggleBulletList().run() },
  { k: 'numbers', label: 'Numbered list', active: (e) => e.isActive('orderedList'), run: (e) => e.chain().focus().toggleOrderedList().run() },
  { k: 'link', label: 'Link', key: 'K', active: (e) => e.isActive('link'), run: null },
];
const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘' : 'Ctrl+';

const frameBtn = {
  height: 28, minWidth: 28, padding: '0 8px', borderRadius: 6, border: '1px solid #e5e7eb',
  background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const iconBtn = {
  width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none',
  borderRadius: 6, background: 'transparent', color: '#6b7280', cursor: 'pointer', padding: 0, flexShrink: 0,
};
const iconOn = { background: NEUTRAL_LIGHTEST, color: BLUE, boxShadow: `inset 0 0 0 1px ${BLUE}` };

/**
 * @param {object} p
 * @param {(payload: { body_html: string, body: string, attachment: object|null }) => Promise<boolean>} p.onSend
 *        resolves true when sent (the box then clears)
 * @param {(file: File) => Promise<{data: string, mime: string, name: string}>} [p.uploadAttachment] omitted → no Attach file
 * @param {string} [p.attachAccept]   the file picker's accept list (per surface, unchanged)
 * @param {string} [p.attachHint]     the type/size hint beside Attach file (per surface, unchanged; may be '')
 * @param {'button'|'icon-on-phone'} [p.attachLook] app: a paperclip icon on phones
 * @param {'message'|'reply'|'note'} [p.mode]  'note' tints the box violet (DoorlyDesk internal note)
 * @param {React.ReactNode} [p.topLine] a quiet line above the box (the ticket's "To: … · Signs as …")
 * @param {string} [p.placeholder] @param {string} [p.sendLabel] @param {string} [p.sendColor]
 * @param {string} [p.sendClassName]  the app keeps its own Send button classes
 * @param {boolean} [p.disabled] @param {boolean} [p.autoFocus] @param {number} [p.minLines] @param {number} [p.maxLines]
 * @param {number} [p.fontSize]  16 on the app (iOS zooms below 16px), 13 in DoorlyDesk
 * @param {string} [p.footerNote] a short line in place of the type/size hint
 * @param {string} [p.formatBarKey] @param {string} [p.testId]
 * @param {(attachment: object) => string} [p.attachmentLabel] how the picked file is named (the app: the borrower name)
 */
export default function RichComposer({
  onSend, uploadAttachment = null, attachAccept = '.pdf,.jpg,.jpeg,.png,.heic', attachHint = '', attachLook = 'button',
  mode = 'message', topLine = null, placeholder = 'Type a message…', sendLabel = 'Send', sendColor = BLUE,
  sendClassName = null, disabled = false, autoFocus = false, minLines = 2, maxLines = 6, fontSize = 13,
  footerNote = null, formatBarKey = FORMAT_BAR_KEY, testId = 'composer', attachmentLabel = null,
}) {
  const [barOpen, setBarOpen] = useState(() => readBar(formatBarKey));
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [linkDialog, setLinkDialog] = useState(null);   // { text, url, edit, error }
  const [, force] = useState(0);
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 639px)').matches);
  const fileRef = useRef(null);
  const sendRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const openLinkRef = useRef(null);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 639px)');
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);

  const lineH = Math.round(fontSize * 1.5);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, blockquote: false, code: false, codeBlock: false, strike: false, horizontalRule: false,
        dropcursor: false, gapcursor: false, trailingNode: false,
        link: {
          openOnClick: false, autolink: true, linkOnPaste: true, defaultProtocol: 'https',
          protocols: ['http', 'https', 'mailto'],
          isAllowedUri: (url) => !!normaliseLink(url) && SAFE_URL.test(normaliseLink(url)),
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' },
        },
      }),
      Placeholder.configure({ placeholder }),
      // Ctrl/Cmd+Enter sends; Ctrl/Cmd+K opens the link dialog. Enter stays a new line (ProseMirror's own).
      Extension.create({
        name: 'composerKeys',
        addKeyboardShortcuts() {
          return {
            'Mod-Enter': () => { if (sendRef.current) sendRef.current(); return true; },
            'Mod-k': () => { if (openLinkRef.current) openLinkRef.current(); return true; },
          };
        },
      }),
    ],
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        'data-testid': 'composer-text',
        role: 'textbox', 'aria-multiline': 'true', 'aria-label': placeholder,
        class: 'doorly-rich-message doorly-composer-editor',
      },
      transformPastedHTML: wordListsToHtml,
      // Files are attached with Attach file only — never dropped or pasted into the text.
      handleDrop: (view, event) => !!(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length),
      handlePaste: (view, event) => !!(event.clipboardData && event.clipboardData.files && event.clipboardData.files.length && !event.clipboardData.getData('text/plain')),
    },
    onUpdate: () => force((n) => n + 1),
    onSelectionUpdate: () => force((n) => n + 1),
  });

  const empty = !editor || editor.isEmpty || !editor.getText().trim();
  const canSend = !!((!empty) || attachment) && !sending && !uploading && !disabled;

  const send = useCallback(async () => {
    if (!editor || !canSend) return;
    setSending(true); setError('');
    const payload = {
      body_html: empty ? '' : editor.getHTML(),
      body: empty ? '' : editor.getText({ blockSeparator: '\n\n' }).trim(),
      attachment: attachment || null,
    };
    let sent = false;
    try { sent = await onSend(payload); } catch (err) { if (mounted.current) setError((err && err.message) || 'The message was not sent.'); }
    // The surface may have remounted the box on send (the app's first message opens its thread): nothing to clear then.
    if (!mounted.current || editor.isDestroyed) return;
    if (sent) { editor.commands.clearContent(true); setAttachment(null); }
    setSending(false);
  }, [editor, canSend, empty, attachment, onSend]);
  useEffect(() => { sendRef.current = isPhone ? null : send; }, [send, isPhone]);

  const openLink = useCallback(() => {
    if (!editor) return;
    const isLink = editor.isActive('link');
    if (isLink) editor.chain().focus().extendMarkRange('link').run();
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, ' ');
    setLinkDialog({ text, url: isLink ? (editor.getAttributes('link').href || '') : '', edit: isLink, error: '' });
  }, [editor]);
  useEffect(() => { openLinkRef.current = openLink; }, [openLink]);

  const saveLink = () => {
    if (!editor || !linkDialog) return;
    const href = normaliseLink(linkDialog.url);
    if (!href) { setLinkDialog({ ...linkDialog, error: 'Use a web address starting with https:// or an email address.' }); return; }
    const label = (linkDialog.text || '').trim() || href.replace(/^mailto:/i, '');
    const { from, to } = editor.state.selection;
    const chain = editor.chain().focus();
    if (from === to || label !== editor.state.doc.textBetween(from, to, ' ')) {
      chain.deleteRange({ from, to }).insertContentAt(from, { type: 'text', text: label, marks: [{ type: 'link', attrs: { href } }] }).run();
    } else {
      chain.setLink({ href }).run();
    }
    setLinkDialog(null);
  };
  const removeLink = () => { if (editor) editor.chain().focus().extendMarkRange('link').unsetLink().run(); setLinkDialog(null); };

  const toggleBar = () => { const next = !barOpen; setBarOpen(next); writeBar(formatBarKey, next); };

  const pickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file || !uploadAttachment) return;
    setError(''); setUploading(true);
    try { setAttachment(await uploadAttachment(file)); } catch (err) { setError((err && err.message) || 'The file could not be attached.'); }
    setUploading(false);
  };

  const note = mode === 'note';
  const boxStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
    border: `1px solid ${note ? VIOLET : '#e5e7eb'}`, background: note ? VIOLET_LIGHT : '#fff',
    fontSize, lineHeight: `${lineH}px`, color: '#111827', cursor: 'text',
    minHeight: minLines * lineH + 20, maxHeight: maxLines * lineH + 20, overflowY: 'auto',
  };
  const phoneIcons = isPhone && attachLook === 'icon-on-phone';

  return (
    <div data-testid={testId} className="doorly-composer" data-mode={mode} style={{ position: 'relative' }}>
      {topLine}
      {barOpen && editor && (
        <div role="toolbar" aria-label="Formatting" data-testid="composer-format-row"
          style={{ display: 'flex', gap: 2, marginBottom: 6, flexWrap: 'nowrap', alignItems: 'center' }}>
          {FORMATS.map((f) => {
            const on = f.active(editor);
            const title = f.key ? `${f.label} (${MOD}${f.key})` : f.label;
            return (
              <button key={f.k} type="button" title={title} aria-label={f.label} aria-pressed={on ? 'true' : 'false'}
                data-testid={`composer-${f.k}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => (f.k === 'link' ? openLink() : f.run(editor))}
                style={{ ...iconBtn, ...(on ? iconOn : null) }}>
                {ICONS[f.k](18)}
              </button>
            );
          })}
        </div>
      )}
      <div style={boxStyle} onClick={() => editor && !editor.isDestroyed && editor.commands.focus()} data-testid="composer-box">
        <EditorContent editor={editor} />
      </div>
      {linkDialog && (
        <LinkDialog dialog={linkDialog} setDialog={setLinkDialog} onSave={saveLink} onRemove={removeLink} phone={isPhone} />
      )}
      {error && <div data-testid="composer-error" role="alert" style={{ marginTop: 6, fontSize: 12, color: STATUS_COLORS.adverse.text }}>{error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button type="button" title="Formatting" aria-label="Formatting" aria-pressed={barOpen ? 'true' : 'false'}
          data-testid="composer-format-toggle" onMouseDown={(e) => e.preventDefault()} onClick={toggleBar}
          style={{ ...frameBtn, padding: 0, width: phoneIcons ? 36 : 28, height: phoneIcons ? 36 : 28, justifyContent: 'center', borderRadius: phoneIcons ? 8 : 6,
            ...(barOpen ? { borderColor: BLUE, color: BLUE, background: NEUTRAL_LIGHTEST } : { color: '#6b7280' }) }}>
          {ICONS.format(phoneIcons ? 20 : 16)}
        </button>
        {uploadAttachment && (<>
          <input ref={fileRef} type="file" accept={attachAccept} style={{ display: 'none' }} data-testid="composer-file" onChange={pickFile} />
          <button type="button" title="Attach file" aria-label="Attach file" data-testid="composer-attach" disabled={uploading}
            onClick={() => fileRef.current && fileRef.current.click()}
            style={phoneIcons ? { ...frameBtn, padding: 0, width: 36, height: 36, justifyContent: 'center', borderRadius: 8, color: '#6b7280' } : frameBtn}>
            {phoneIcons ? (uploading ? '…' : ICONS.clip(20)) : (uploading ? 'Attaching…' : <>{attachLook === 'icon-on-phone' ? ICONS.clip(14) : null}Attach file</>)}
          </button>
        </>)}
        {attachment && (
          <span data-testid="composer-attachment" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', minWidth: 0, maxWidth: 220 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachmentLabel ? attachmentLabel(attachment) : attachment.name}</span>
            <button type="button" aria-label="Remove attachment" onClick={() => setAttachment(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14 }}>×</button>
          </span>
        )}
        <span style={{ flex: 1, fontSize: 11, color: '#9ca3af', minWidth: 0 }}>{attachment ? '' : (footerNote || (uploadAttachment ? attachHint : ''))}</span>
        <button type="button" onClick={send} disabled={!canSend} data-testid="composer-send"
          className={sendClassName || undefined}
          style={sendClassName ? undefined : {
            padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: note ? VIOLET : sendColor, color: '#fff',
            cursor: canSend ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: canSend ? 1 : 0.5, flexShrink: 0,
          }}>
          {sending ? 'Sending…' : sendLabel}
        </button>
      </div>
    </div>
  );
}

function LinkDialog({ dialog, setDialog, onSave, onRemove, phone }) {
  const valid = !!normaliseLink(dialog.url);
  const input = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${dialog.error || (dialog.url.trim() && !valid) ? STATUS_COLORS.adverse.text : '#e5e7eb'}`,
    borderRadius: 8, fontSize: phone ? 16 : 13, color: '#111827', outline: 'none', marginTop: 4,
  };
  const keys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onSave(); }
    if (e.key === 'Escape') { e.preventDefault(); setDialog(null); }
  };
  return (
    <div role="dialog" aria-label={dialog.edit ? 'Edit link' : 'Add link'} data-testid="composer-link-dialog"
      style={{ position: phone ? 'fixed' : 'absolute', zIndex: 50, left: phone ? 16 : 0, right: phone ? 16 : 'auto', bottom: phone ? 16 : 'auto',
        top: phone ? 'auto' : 40, width: phone ? 'auto' : 320, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        boxShadow: '0 8px 24px rgba(17,24,39,.14)', padding: 14, fontSize: 13, color: '#111827', boxSizing: 'border-box' }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>{dialog.edit ? 'Edit link' : 'Add link'}</div>
      <label style={{ display: 'block', fontSize: 12, color: '#374151', fontWeight: 600 }}>Text
        <input data-testid="composer-link-text" style={{ ...input, borderColor: '#e5e7eb' }} value={dialog.text} placeholder="What the reader sees"
          onChange={(e) => setDialog({ ...dialog, text: e.target.value })} onKeyDown={keys} />
      </label>
      <label style={{ display: 'block', fontSize: 12, color: '#374151', fontWeight: 600, marginTop: 10 }}>Link address
        <input data-testid="composer-link-url" autoFocus style={input} value={dialog.url} placeholder="https://… or name@example.com"
          onChange={(e) => setDialog({ ...dialog, url: e.target.value, error: '' })} onKeyDown={keys} />
      </label>
      {(dialog.error || (dialog.url.trim() && !valid)) && <div role="alert" data-testid="composer-link-error" style={{ marginTop: 6, fontSize: 12, color: STATUS_COLORS.adverse.text }}>{dialog.error || 'Use a web address starting with https:// or an email address.'}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
        {dialog.edit && <button type="button" data-testid="composer-link-remove" onClick={onRemove}
          style={{ border: 'none', background: 'none', color: STATUS_COLORS.adverse.text, fontSize: 12, cursor: 'pointer', padding: 0 }}>Remove link</button>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setDialog(null)} style={{ ...frameBtn, height: 32, padding: '0 12px' }}>Cancel</button>
        <button type="button" data-testid="composer-link-save" onClick={onSave} disabled={!valid}
          style={{ height: 32, padding: '0 16px', borderRadius: 8, border: 'none', background: BLUE, color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: valid ? 'pointer' : 'not-allowed', opacity: valid ? 1 : 0.5 }}>Save</button>
      </div>
    </div>
  );
}
